import '@mui/material/styles'
import { createTheme } from '@mui/material/styles'

declare module '@mui/material/styles' {
    interface Palette {
        surface: SurfacePalette
        border: BorderPalette
        textColor: TextPalette
        icon: IconPalette
        button: ButtonPalette
        interaction: InteractionPalette
    }
    interface PaletteOptions {
        surface?: SurfacePalette
        border?: BorderPalette
        textColor?: TextPalette
        icon?: IconPalette
        button?: ButtonPalette
        interaction?: InteractionPalette
        sidebar?: SidebarPalette
        card?: CardPalette
    }

    interface SurfacePalette {
        default: string
        hover: string
        active: string
        disable: string
        primary: string
        secondary: string
        info: string
        error: string
        headRow: string
    }

    interface BorderPalette {
        default: string
        activePrimary: string
        activeSecondary: string
        primary: string
        disable: string
        error: string
    }

    interface TextPalette {
        default: string
        secondary: string
        tertiary: string
        subtle: string
        disable: string
        onColor: string
        error: string
        info: string
    }

    interface IconPalette {
        primary: string
        secondary: string
        tertiary: string
        subtle: string
        subdued: string
        onColor: string
        disable: string
        error: string
        info: string
    }

    interface ButtonPalette {
        primary: string
        secondary: string
        tertiary: string
        link: string
        primaryDisable: string
        secondaryDisable: string
    }

    interface InteractionPalette {
        default: string
        primary: string
        active: string
        disable: string
    }

    interface SidebarPalette {
        default: string
        action: string
    }

    interface CardPalette {
        default: string
        border: string
    }
}

declare module '@mui/material/styles' {
    interface TypographyVariants {
        d1: React.CSSProperties
        h1Alt: React.CSSProperties
        h2Regular: React.CSSProperties
        b1Medium: React.CSSProperties
        b1Bold: React.CSSProperties
        b1Regular: React.CSSProperties
        b2Regular: React.CSSProperties
        b2SemiBold: React.CSSProperties
        b2Bold: React.CSSProperties
        b2PoppinsSemiBold: React.CSSProperties
        btn1SemiBold: React.CSSProperties
        btn1Regular: React.CSSProperties
        btn2Medium: React.CSSProperties
        c1PoppinsMedium: React.CSSProperties
        c2Regular: React.CSSProperties
        c2Bold: React.CSSProperties
        label: React.CSSProperties
        input: React.CSSProperties
        tableHeader: React.CSSProperties
        tableBodyCell: React.CSSProperties
        selectBoxRow: React.CSSProperties
        cardTitle: React.CSSProperties
        cardSubtitle: React.CSSProperties
        muted: React.CSSProperties
        productTitle: React.CSSProperties
        productSubTotal: React.CSSProperties
        productTotal: React.CSSProperties
        error: React.CSSProperties
        body3: React.CSSProperties
        footer: React.CSSProperties
    }

    interface TypographyVariantsOptions {
        d1?: React.CSSProperties
        h1Alt?: React.CSSProperties
        h2Regular?: React.CSSProperties
        b1Medium?: React.CSSProperties
        b1Bold?: React.CSSProperties
        b1Regular?: React.CSSProperties
        b2Regular?: React.CSSProperties
        b2SemiBold?: React.CSSProperties
        b2Bold?: React.CSSProperties
        b2PoppinsSemiBold?: React.CSSProperties
        btn1SemiBold?: React.CSSProperties
        btn1Regular?: React.CSSProperties
        btn2Medium?: React.CSSProperties
        c1PoppinsMedium?: React.CSSProperties
        c2Regular?: React.CSSProperties
        c2Bold?: React.CSSProperties
        label?: React.CSSProperties
        input?: React.CSSProperties
        tableHeader?: React.CSSProperties
        tableBodyCell?: React.CSSProperties
        selectBoxRow?: React.CSSProperties
        cardTitle?: React.CSSProperties
        cardSubtitle?: React.CSSProperties
        muted?: React.CSSProperties
        productTitle?: React.CSSProperties
        productSubTotal?: React.CSSProperties
        productTotal?: React.CSSProperties
        link?: React.CSSProperties
        error?: React.CSSProperties
        body3?: React.CSSProperties
        footer?: React.CSSProperties
        loadingModalTitle?: React.CSSProperties
        loadingModalSubtitle?: React.CSSProperties
    }
}

declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        d1: true
        h1Alt: true
        h2Regular: true
        b1Medium: true
        b1Bold: true
        b1Regular: true
        b2Regular: true
        b2SemiBold: true
        b2Bold: true
        b2PoppinsSemiBold: true
        btn1SemiBold: true
        btn1Regular: true
        btn2Medium: true
        c1PoppinsMedium: true
        c2Regular: true
        c2Bold: true
        label: true
        input: true
        tableHeader: true
        tableBodyCell: true
        selectBoxRow: true
        cardTitle: true
        cardSubtitle: true
        muted: true
        productTitle: true
        productSubTotal: true
        productTotal: true
        link: true
        error: true
        body3: true
        footer: true
        loadingModalTitle: true
        loadingModalSubtitle: true
    }
}

export const theme = createTheme({
    palette: {
        background: {
            default: '#F0F2F5'
        },
        surface: {
            default: '#FFFFFF',
            hover: '#F1F5F9',
            active: '#E5E8F1',
            disable: '#F2F4F8',
            primary: '#2273D7',
            secondary: '#00358F',
            info: '#E6F5FA',
            error: '#FEE6E6',
            headRow: '#F9FAFB'
        },
        border: {
            default: '#CCDEEF',
            activePrimary: '#00358F',
            activeSecondary: '#99BCDF',
            primary: '#5A8FC3',
            disable: '#D2D9E1',
            error: '#FE5D7C'
        },
        textColor: {
            default: '#0F172A',
            secondary: '#00358F',
            tertiary: '#5A8FC3',
            subtle: '#6B7280',
            disable: '#9C9CCC',
            onColor: '#FFFFFF',
            error: '#FE1000',
            info: '#0071F4'
        },
        icon: {
            primary: '#00172A',
            secondary: '#00358F',
            tertiary: '#5A8FC3',
            subtle: '#90BCCF',
            subdued: '#8892A5',
            onColor: '#FFFFFF',
            disable: '#D0D8E8',
            error: '#FE5D7C',
            info: '#0071F4'
        },
        button: {
            primary: '#2273D7',
            secondary: '#00358F',
            tertiary: '#FFFFFF',
            link: '#1C9CFC',
            primaryDisable: '#CCDEEF',
            secondaryDisable: '#B9C0CC'
        },
        interaction: {
            default: '#FFFFFF',
            primary: '#5A8FC3',
            active: '#00358F',
            disable: '#DEE1F4'
        },
        sidebar: {
            default: '#FFFFFF',
            action: '#F1F5F9'
        },
        card: {
            default: '#FFFFFF',
            border: '#E5E8F1'
        }
    },
    typography: {
        fontFamily: 'Inter, Poppins, NotoSansThai',
        d1: {
            fontFamily: 'Inter, Poppins, NotoSansThai',
            fontWeight: 'bold',
            fontSize: '30px',
            lineHeight: '36px',
            letterSpacing: '0px'
        },
        h1: {
            fontFamily: 'Inter, Poppins, NotoSansThai',
            fontWeight: 'bold',
            fontSize: '22px',
            lineHeight: '30px',
            letterSpacing: '0px'
        },
        h1Alt: {
            fontFamily: 'Inter, Poppins, NotoSansThai',
            fontWeight: 'bold',
            fontSize: '22px',
            lineHeight: '30px',
            letterSpacing: '0px'
        },
        h2: {
            fontFamily: 'Inter, Poppins, NotoSansThai',
            fontWeight: 700,
            fontSize: '24px',
            lineHeight: '24px',
            letterSpacing: '0px'
        },
        h2Regular: {
            fontFamily: 'Inter, Poppins, NotoSansThai',
            fontWeight: 400,
            fontSize: '18px',
            lineHeight: '24px',
            letterSpacing: '0px'
        },
        b1Medium: {
            fontFamily: 'Inter, Poppins, NotoSansThai',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0px'
        },
        b1Bold: {
            fontFamily: 'Inter, Poppins, NotoSansThai',
            fontWeight: 700,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0px'
        },
        b1Regular: {
            fontFamily: 'Inter, Poppins, NotoSansThai',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0px'
        },
        b2Regular: {
            fontFamily: 'Inter, Poppins, NotoSansThai',
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '16px',
            letterSpacing: '0px'
        },
        b2SemiBold: {
            fontFamily: 'Inter, Poppins, NotoSansThai',
            fontWeight: 600,
            fontSize: '12px',
            lineHeight: '16px',
            letterSpacing: '0px'
        },
        b2Bold: {
            fontFamily: 'Inter, Poppins, NotoSansThai',
            fontWeight: 700,
            fontSize: '12px',
            lineHeight: '16px',
            letterSpacing: '0px'
        },
        b2PoppinsSemiBold: {
            fontFamily: 'Inter, Poppins, NotoSansThai',
            fontWeight: 600,
            fontSize: '12px',
            lineHeight: '16px',
            letterSpacing: '0px'
        },
        btn1SemiBold: {
            fontFamily: 'Inter, Poppins, NotoSansThai',
            fontWeight: 600,
            fontSize: '18px',
            lineHeight: '24px',
            letterSpacing: '0px'
        },
        btn1Regular: {
            fontFamily: 'Inter, Poppins, NotoSansThai',
            fontWeight: 400,
            fontSize: '18px',
            lineHeight: '24px',
            letterSpacing: '0px'
        },
        btn2Medium: {
            fontFamily: 'Inter, Poppins, NotoSansThai',
            fontWeight: 500,
            fontSize: '18px',
            lineHeight: '24px',
            letterSpacing: '0px'
        },
        c1PoppinsMedium: {
            fontFamily: 'Inter, Poppins, NotoSansThai',
            fontWeight: 500,
            fontSize: '8px',
            lineHeight: '12px',
            letterSpacing: '0px',
            textTransform: 'uppercase'
        },
        c2Regular: {
            fontFamily: 'Inter, Poppins, NotoSansThai',
            fontWeight: 400,
            fontSize: '8px',
            lineHeight: '12px',
            letterSpacing: '0px'
        },
        c2Bold: {
            fontFamily: 'Inter, Poppins, NotoSansThai',
            fontWeight: 700,
            fontSize: '8px',
            lineHeight: '12px',
            letterSpacing: '0px'
        },
        label: {
            fontFamily: 'Inter, Poppins, NotoSansThai',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0px',
            color: '#0F172A'
        },
        input: {
            fontFamily: 'Inter, Poppins, NotoSansThai',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0px',
            color: '#0F172A'
        },
        body1: {
            fontFamily: 'Inter, Poppins, NotoSansThai',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0px'
        },
        body2: {
            fontFamily: 'Inter, Poppins, NotoSansThai',
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '16px',
            letterSpacing: '0px'
        },
        body3: {
            fontFamily: 'Poppins',
            fontWeight: 600,
            fontSize: '16px',
            lineHeight: '14px',
            letterSpacing: '0px'
        },
        tableHeader: {
            fontFamily: 'Inter, Poppins, NotoSansThai',
            fontWeight: 700,
            fontSize: '10px',
            lineHeight: '12px',
            letterSpacing: '0px',
            color: '#6B7280'
        },
        tableBodyCell: {
            fontFamily: 'Inter, Poppins, NotoSansThai',
            fontWeight: 500,
            fontSize: '12px',
            lineHeight: '14px',
            letterSpacing: '0px',
            color: '#0F172A'
        },
        selectBoxRow: {
            fontFamily: 'Inter, Poppins, NotoSansThai',
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '20px',
            letterSpacing: '0px',
            color: '#0F172A'
        },
        cardTitle: {
            fontFamily: 'Poppins',
            fontWeight: 700,
            fontSize: '24px',
            lineHeight: '24px',
            letterSpacing: '0px'
        },
        cardSubtitle: {
            fontFamily: 'Inter',
            fontWeight: 400,
            fontSize: '20px',
            lineHeight: '20px',
            letterSpacing: '0px'
        },
        muted: {
            fontFamily: 'Inter',
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '24px',
            letterSpacing: '0px',
            color: '#8A9BB0'
        },
        productTitle: {
            fontFamily: 'Inter',
            fontWeight: 700,
            fontSize: '14px',
            lineHeight: '24px',
            letterSpacing: '0px',
            color: 'rgba(0, 55, 100, 1)'
        },
        productTotal: {
            fontFamily: 'Poppins',
            fontWeight: 700,
            fontSize: '14px',
            lineHeight: '24px',
            letterSpacing: '0px',
            color: 'rgba(0, 55, 100, 1)'
        },
        productSubTotal: {
            fontFamily: 'Poppins',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '24px',
            letterSpacing: '0px',
            color: 'rgba(0, 55, 100, 1)'
        },
        link: {
            fontFamily: 'Inter',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '24px',
            letterSpacing: '0px',
            color: 'rgba(0, 55, 100, 1)',
            textDecoration: 'none'
        },
        error: {
            fontFamily: 'Inter',
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '24px',
            letterSpacing: '0px',
            color: 'rgba(254, 93, 124, 1)'
        },
        footer: {
            fontFamily: 'Inter',
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '20px',
            letterSpacing: '0px',
            color: 'rgba(255, 255, 255, 1)'
        },
        loadingModalTitle: {
            fontFamily: 'Inter',
            fontWeight: 700,
            fontSize: '32px',
            letterSpacing: '0px',
            color: 'white'
        },
        loadingModalSubtitle: {
            fontFamily: 'Inter',
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '28px',
            letterSpacing: '0px',
            color: 'white'
        }
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: '99px',
                    backgroundColor: 'rgba(90, 143, 195, 1)',
                    color: '#FFFFFF',
                    fontFamily: 'Poppins',
                    fontWeight: 600,
                    fontSize: '18px',
                    lineHeight: '20px',
                    boxShadow: 'none',
                    height: '40px',
                    textTransform: 'none',
                    '&:active': {
                        boxShadow: 'none'
                    },
                    '&:disabled': {
                        backgroundColor: 'rgba(204, 222, 239, 1)',
                        color: '#FFFFFF'
                    },
                    '&:focus': {
                        outline: 'none!important'
                    }
                }
            }
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    '&:focus, &:focus-visible, span': {
                        outline: 'none!important'
                    }
                }
            }
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    '&.active': {
                        backgroundColor: 'rgba(255, 255, 255, 0.25)',
                        '.MuiListItemText-primary': {
                            fontWeight: 600
                        }
                    },
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.25)'
                    }
                }
            }
        },
        MuiListItemText: {
            styleOverrides: {
                primary: {
                    fontFamily: 'Inter, Poppins, NotoSansThai',
                    fontSize: '14px',
                    lineHeight: '16px',
                    letterSpacing: '0px',
                    color: '#FFFFFF',
                    fontWeight: 300,
                    '.active': {
                        fontWeight: 600
                    }
                }
            }
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: '12px',
                    padding: '24px 16px',
                    boxShadow: 'none'
                }
            }
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    padding: '0px!important'
                }
            }
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    backgroundColor: '#ffffff',
                    borderRadius: '6px',
                    border: '1px solid #CCDEEF',
                    height: '50px',
                    '&.MuiFilledInput-root': {
                        '&::after': {
                            borderBottom: 'none!important'
                        },
                        '&::before': {
                            borderBottom: 'none!important'
                        },
                        '&:hover': {
                            backgroundColor: '#ffffff!important'
                        }
                    }
                },
                input: {
                    backgroundColor: '#ffffff',
                    height: '50px',
                    color: '#003B6F',
                    fontFamily: 'Inter, Poppins, NotoSansThai',
                    fontWeight: 400,
                    fontSize: '14px'
                }
            }
        },

        MuiFormLabel: {
            styleOverrides: {
                root: {
                    fontFamily: 'Inter, Poppins, NotoSansThai',
                    fontSize: '14px',
                    color: '#003B6F',
                    '&.MuiInputLabel-shrink': {
                        transform: 'translate(12px, 7px) scale(0.86)',
                        color: '#62748E'
                    }
                }
            }
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: '#E9F1FA'
                }
            }
        }
    }
})
